import {
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react'
import { MenuEventArgs } from '@syncfusion/ej2-react-splitbuttons'
import { NotificationToastData } from '@components/common/NotificationToast/NotificationToast.component'
import { IToothDetails } from '@dataTypes/appointment.type'
import { Discount } from '@dataTypes/discount.type'
import { InsurancePlan } from '@dataTypes/insurance.types'
import {
  IPhases,
  ITreatmentPlan,
  TxPlanProcedureModalContext,
  TxPlanStatus,
} from '@dataTypes/treatmentPlan.types'
import { updateTreatmentPlan } from '@store/actions/treatment-plan.action'
import type { AppDispatch } from '@store/index'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { buildPresetToothDetailsForTeeth } from '@utils/tx-plan-tooth-details.utils'
import {
  collectTxPlanVisiblePhaseAccordionKeys,
  movePhaseByVisibleStep,
  txPlanDiscountFormFromPlan,
} from '@utils/treatment-plan.utils'
import { getVisibleTxPlanPhasePickerOptions } from './TxPlanToothPhasePicker/TxPlanToothPhasePicker.component'
import {
  createEmptyPhase,
  createNewPhaseKey,
  duplicatePhaseAtIndex,
  getPhaseEditKey,
  markHistoryAfterFormUpdate,
  renumberPhases,
} from './treatmentPlanMain.helpers'
import type {
  PendingPhaseDelete,
  PendingStatusChange,
  TreatmentPlanHistoryActions,
  TxPlanTabFormState,
} from './TreatmentPlanMain.types'

export type UseTreatmentPlanPhasesParams = {
  localDrafts: Record<string, boolean>
  editModes: Record<string, boolean>
  formStatesRef: MutableRefObject<Record<string, TxPlanTabFormState>>
  setFormStates: Dispatch<SetStateAction<Record<string, TxPlanTabFormState>>>
  setEditModes: Dispatch<SetStateAction<Record<string, boolean>>>
  setPendingStatusChange: Dispatch<SetStateAction<PendingStatusChange | null>>
  setPendingToothAdd: Dispatch<
    SetStateAction<{ tabKey: string; toothNums: number[] } | null>
  >
  historyActionsRef: MutableRefObject<TreatmentPlanHistoryActions>
  toggleEditModeRef: MutableRefObject<(tabKey: string) => void>
  dispatch: AppDispatch
  showToast: (toast: NotificationToastData) => void
  insuranceList: InsurancePlan[]
  activeBillingDiscounts: Discount[]
}

export type UseTreatmentPlanPhasesResult = {
  phaseVisibility: Record<string, boolean>
  phaseVisibilityRef: MutableRefObject<Record<string, boolean>>
  setPhaseVisibility: Dispatch<SetStateAction<Record<string, boolean>>>
  pendingPhaseDelete: PendingPhaseDelete | null
  setPendingPhaseDelete: Dispatch<SetStateAction<PendingPhaseDelete | null>>
  editingPhaseKey: string | null
  editingPhaseValue: string
  setEditingPhaseValue: Dispatch<SetStateAction<string>>
  handleAddPhase: (tabKey: string) => void
  openProcedureModal: (
    tabKey: string,
    context: TxPlanProcedureModalContext,
  ) => void
  handleAddProcedureFromTooth: (
    tabKey: string,
    toothNums: number[],
    phaseIdx: number,
  ) => void
  handleAddNewPhaseFromToothPicker: (
    tabKey: string,
    toothNums: number[],
  ) => void
  handleAddPhaseAndOpenProcedureModal: (
    tabKey: string,
    presetToothDetails?: IToothDetails[],
    options?: { clearPendingToothAdd?: boolean },
  ) => void
  beginToothDrivenProcedureAdd: (tabKey: string, toothNums: number[]) => void
  handleAddProcedure: (tabKey: string, idx: number) => void
  confirmDeletePhase: () => void
  handlePhaseStatusChange: (
    newStatus: TxPlanStatus,
    tabKey: string,
    originalIdx: number,
    phase: IPhases,
    plan: ITreatmentPlan | null,
    isEditMode: boolean,
  ) => void
  handleMenuSelect: (
    args: MenuEventArgs,
    tabKey: string,
    originalIdx: number,
    phase: IPhases,
    plan: ITreatmentPlan | null,
    isEditMode: boolean,
  ) => void
  togglePhaseVisibility: (phaseId: string) => void
  getPhaseVisibility: (phaseId: string) => boolean
  toggleAllPhaseAccordions: (tabKey: string) => void
  handleStartEditPhaseName: (
    tabKey: string,
    phase: IPhases,
    originalIdx: number,
    currentName: string,
  ) => void
  handleSavePhaseName: (
    tabKey: string,
    originalIdx: number,
    phaseIdx: number,
  ) => void
  handleCancelEditPhaseName: () => void
}

function buildFormStateFromPlan(
  plan: ITreatmentPlan,
  insuranceList: InsurancePlan[],
  activeBillingDiscounts: Discount[],
): TxPlanTabFormState {
  return {
    planName: plan.tp_heading || 'Plan 1',
    insuranceProvider: plan.insurance_plan_id || null,
    selectedInsurance:
      insuranceList.find(
        (p) => String(p.insurance_plan_id) === plan.insurance_plan_id,
      ) || null,
    ...txPlanDiscountFormFromPlan(plan, activeBillingDiscounts),
    phases:
      plan.phases?.map((p) => ({
        ...p,
        scheduled_date:
          !p.scheduled_date || isNaN(Date.parse(p.scheduled_date))
            ? ''
            : new Date(p.scheduled_date).toISOString().split('T')[0],
      })) || [],
    activeAccordionKeys: [],
    procedureModal: null,
  }
}

export function useTreatmentPlanPhases({
  localDrafts,
  editModes,
  formStatesRef,
  setFormStates,
  setEditModes,
  setPendingStatusChange,
  setPendingToothAdd,
  historyActionsRef,
  toggleEditModeRef,
  dispatch,
  showToast,
  insuranceList,
  activeBillingDiscounts,
}: UseTreatmentPlanPhasesParams): UseTreatmentPlanPhasesResult {
  const [phaseVisibility, setPhaseVisibility] = useState<
    Record<string, boolean>
  >({})
  const phaseVisibilityRef = useRef(phaseVisibility)
  phaseVisibilityRef.current = phaseVisibility
  const [pendingPhaseDelete, setPendingPhaseDelete] =
    useState<PendingPhaseDelete | null>(null)
  const [editingPhaseKey, setEditingPhaseKey] = useState<string | null>(null)
  const [editingPhaseValue, setEditingPhaseValue] = useState<string>('')

  const ensureEditModeForTab = useCallback(
    (tabKey: string): { isLocalDraft: boolean; isEditMode: boolean } => {
      const isLocalDraft = localDrafts[tabKey] || false
      const isEditMode = isLocalDraft || editModes[tabKey] || false
      if (!isLocalDraft && !isEditMode) {
        setEditModes((prev) => ({ ...prev, [tabKey]: true }))
        historyActionsRef.current.markHistoryInitPending(tabKey)
      }
      return { isLocalDraft, isEditMode }
    },
    [editModes, historyActionsRef, localDrafts, setEditModes],
  )

  const handleAddPhase = useCallback(
    (tabKey: string): void => {
      const { isLocalDraft, isEditMode } = ensureEditModeForTab(tabKey)
      if (isEditMode || isLocalDraft) {
        historyActionsRef.current.queueHistorySnapshotBefore(tabKey)
      }
      const newPhaseKey = createNewPhaseKey()
      setFormStates((prev) => {
        const formState = prev[tabKey]
        if (!formState) return prev
        const visiblePhases = formState.phases.filter((p) => !p.is_hidden)
        return {
          ...prev,
          [tabKey]: {
            ...formState,
            phases: [
              ...formState.phases,
              createEmptyPhase(visiblePhases.length, newPhaseKey),
            ],
            activeAccordionKeys: [
              ...formState.activeAccordionKeys,
              newPhaseKey,
            ],
          },
        }
      })
      markHistoryAfterFormUpdate(
        historyActionsRef.current.markHistorySavePending,
        isEditMode,
        isLocalDraft,
        tabKey,
      )
    },
    [ensureEditModeForTab, historyActionsRef, setFormStates],
  )

  const openProcedureModal = useCallback(
    (tabKey: string, context: TxPlanProcedureModalContext): void => {
      const { isLocalDraft, isEditMode } = ensureEditModeForTab(tabKey)
      if (isEditMode || isLocalDraft) {
        historyActionsRef.current.queueHistorySnapshotBefore(tabKey)
      }
      setFormStates((prev) => {
        const formState = prev[tabKey]
        if (!formState) return prev
        return {
          ...prev,
          [tabKey]: {
            ...formState,
            procedureModal: context,
          },
        }
      })
      markHistoryAfterFormUpdate(
        historyActionsRef.current.markHistorySavePending,
        isEditMode,
        isLocalDraft,
        tabKey,
      )
    },
    [ensureEditModeForTab, historyActionsRef, setFormStates],
  )

  const handleAddProcedureFromTooth = useCallback(
    (tabKey: string, toothNums: number[], phaseIdx: number): void => {
      openProcedureModal(tabKey, {
        phaseIndex: phaseIdx,
        presetToothDetails: buildPresetToothDetailsForTeeth(toothNums),
      })
    },
    [openProcedureModal],
  )

  const handleAddPhaseAndOpenProcedureModal = useCallback(
    (
      tabKey: string,
      presetToothDetails?: IToothDetails[],
      options?: { clearPendingToothAdd?: boolean },
    ): void => {
      const { isLocalDraft, isEditMode } = ensureEditModeForTab(tabKey)
      if (isEditMode || isLocalDraft) {
        historyActionsRef.current.queueHistorySnapshotBefore(tabKey)
      }
      const newPhaseKey = createNewPhaseKey()
      setFormStates((prev) => {
        const formState = prev[tabKey]
        if (!formState) return prev
        const visiblePhases = formState.phases.filter((p) => !p.is_hidden)
        const newPhaseIndex = formState.phases.length
        const procedureModal: TxPlanProcedureModalContext = {
          phaseIndex: newPhaseIndex,
          ...(presetToothDetails !== undefined ? { presetToothDetails } : {}),
        }
        return {
          ...prev,
          [tabKey]: {
            ...formState,
            phases: [
              ...formState.phases,
              createEmptyPhase(visiblePhases.length, newPhaseKey),
            ],
            activeAccordionKeys: [
              ...formState.activeAccordionKeys,
              newPhaseKey,
            ],
            procedureModal,
          },
        }
      })
      markHistoryAfterFormUpdate(
        historyActionsRef.current.markHistorySavePending,
        isEditMode,
        isLocalDraft,
        tabKey,
      )
      if (options?.clearPendingToothAdd) {
        setPendingToothAdd(null)
      }
    },
    [
      ensureEditModeForTab,
      historyActionsRef,
      setFormStates,
      setPendingToothAdd,
    ],
  )

  const handleAddNewPhaseFromToothPicker = useCallback(
    (tabKey: string, toothNums: number[]): void => {
      handleAddPhaseAndOpenProcedureModal(
        tabKey,
        buildPresetToothDetailsForTeeth(toothNums),
        { clearPendingToothAdd: true },
      )
    },
    [handleAddPhaseAndOpenProcedureModal],
  )

  const beginToothDrivenProcedureAdd = useCallback(
    (tabKey: string, toothNums: number[]): void => {
      if (toothNums.length === 0) {
        return
      }
      const formState = formStatesRef.current[tabKey]
      if (formState?.procedureModal) {
        return
      }
      const phaseOptions = getVisibleTxPlanPhasePickerOptions(
        formState?.phases ?? [],
        phaseVisibilityRef.current,
      )
      if (phaseOptions.length === 0) {
        handleAddNewPhaseFromToothPicker(tabKey, toothNums)
        return
      }
      setPendingToothAdd({ tabKey, toothNums })
    },
    [formStatesRef, handleAddNewPhaseFromToothPicker, setPendingToothAdd],
  )

  const handleAddProcedure = useCallback(
    (tabKey: string, idx: number): void => {
      openProcedureModal(tabKey, { phaseIndex: idx })
    },
    [openProcedureModal],
  )

  const confirmDeletePhase = useCallback((): void => {
    if (!pendingPhaseDelete) return
    const { plan, phase, tabKey, isEditMode } = pendingPhaseDelete
    if (isEditMode) {
      setFormStates((prev) => {
        const prevState = prev[tabKey]
        if (!prevState) {
          setPendingPhaseDelete(null)
          return prev
        }
        return {
          ...prev,
          [tabKey]: {
            ...prevState,
            phases: renumberPhases(
              prevState.phases.map((p, i) => ({
                ...p,
                is_hidden:
                  i === pendingPhaseDelete.phaseIdx ? true : p.is_hidden,
              })),
            ),
          },
        }
      })
      historyActionsRef.current.markHistorySavePending(tabKey)
      setPendingPhaseDelete(null)
      return
    }
    if (!plan) {
      setPendingPhaseDelete(null)
      return
    }
    const updatedPhases = renumberPhases(
      (plan.phases || []).map((p) => ({
        ...p,
        is_hidden: p === phase ? true : p.is_hidden,
      })),
    )
    dispatch(
      updateTreatmentPlan({
        treatmentPlanId: plan.tp_id,
        treatmentPlan: {
          ...plan,
          phases: updatedPhases,
        },
      }),
    )
      .unwrap()
      .then(() => {
        showToast(
          createSuccessToast(
            'Phase deleted successfully',
            `"${phase.phase_name}" has been deleted successfully.`,
          ),
        )
      })
      .catch((error: unknown) => {
        showToast(
          createFailedToast(
            'Failed to delete phase',
            error instanceof Error
              ? error.message
              : 'Failed to delete phase. Please try again.',
          ),
        )
      })
    setPendingPhaseDelete(null)
  }, [
    dispatch,
    historyActionsRef,
    pendingPhaseDelete,
    setFormStates,
    showToast,
  ])

  const handlePhaseStatusChange = useCallback(
    (
      newStatus: TxPlanStatus,
      tabKey: string,
      originalIdx: number,
      phase: IPhases,
      plan: ITreatmentPlan | null,
      isEditMode: boolean,
    ): void => {
      if (isEditMode) {
        historyActionsRef.current.queueHistorySnapshotBefore(tabKey)
        setFormStates((prev) => {
          const prevState = prev[tabKey]
          if (!prevState) return prev
          return {
            ...prev,
            [tabKey]: {
              ...prevState,
              phases: prevState.phases.map((p, i) =>
                i === originalIdx ? { ...p, status: newStatus } : p,
              ),
            },
          }
        })
        historyActionsRef.current.markHistorySavePending(tabKey)
      } else if (plan) {
        setPendingStatusChange({
          treatmentPlanId: plan.tp_id,
          treatmentPlan: {
            ...plan,
            phases:
              plan.phases?.map((p) => ({
                ...p,
                status: phase === p ? newStatus : p.status,
              })) || [],
          },
          phaseId: phase.phase_id ?? null,
          phaseName: phase.phase_name,
          newStatus,
        })
      }
    },
    [historyActionsRef, setFormStates, setPendingStatusChange],
  )

  const applyPhaseMove = useCallback(
    (tabKey: string, originalIdx: number, direction: 'up' | 'down'): void => {
      setFormStates((prev) => {
        const prevState = prev[tabKey]
        if (!prevState) {
          return prev
        }
        return {
          ...prev,
          [tabKey]: {
            ...prevState,
            phases: movePhaseByVisibleStep(
              prevState.phases,
              originalIdx,
              direction,
            ),
          },
        }
      })
      historyActionsRef.current.markHistorySavePending(tabKey)
    },
    [historyActionsRef, setFormStates],
  )

  const handlePhaseMove = useCallback(
    (
      tabKey: string,
      originalIdx: number,
      direction: 'up' | 'down',
      plan: ITreatmentPlan | null,
      isEditMode: boolean,
    ): void => {
      const runMove = (): void => {
        historyActionsRef.current.queueHistorySnapshotBefore(tabKey)
        applyPhaseMove(tabKey, originalIdx, direction)
      }
      if (isEditMode) {
        runMove()
        return
      }
      if (!plan) {
        return
      }
      toggleEditModeRef.current(tabKey)
      setTimeout(() => {
        runMove()
      }, 0)
    },
    [applyPhaseMove, historyActionsRef, toggleEditModeRef],
  )

  const handleMenuSelect = useCallback(
    (
      args: MenuEventArgs,
      tabKey: string,
      originalIdx: number,
      phase: IPhases,
      plan: ITreatmentPlan | null,
      isEditMode: boolean,
    ): void => {
      const itemId = args.item?.id
      switch (itemId) {
        case 'move-phase-up':
          if (!args.item?.disabled) {
            handlePhaseMove(tabKey, originalIdx, 'up', plan, isEditMode)
          }
          break
        case 'move-phase-down':
          if (!args.item?.disabled) {
            handlePhaseMove(tabKey, originalIdx, 'down', plan, isEditMode)
          }
          break
        case 'duplicate':
          if (isEditMode) {
            setFormStates((prev) => {
              const prevState = prev[tabKey]
              if (!prevState) return prev
              const newPhaseKey = createNewPhaseKey()
              return {
                ...prev,
                [tabKey]: {
                  ...prevState,
                  phases: duplicatePhaseAtIndex(
                    prevState.phases,
                    originalIdx,
                    newPhaseKey,
                  ),
                  activeAccordionKeys: [
                    ...prevState.activeAccordionKeys,
                    newPhaseKey,
                  ],
                },
              }
            })
          } else if (plan) {
            toggleEditModeRef.current(tabKey)
            setTimeout(() => {
              setFormStates((prev) => {
                const prevState = prev[tabKey]
                const newPhaseKey = createNewPhaseKey()
                if (!prevState) {
                  const initializedState = buildFormStateFromPlan(
                    plan,
                    insuranceList,
                    activeBillingDiscounts,
                  )
                  const phaseToDuplicate = initializedState.phases[originalIdx]
                  const visiblePhases = initializedState.phases.filter(
                    (p) => !p.is_hidden,
                  )
                  return {
                    ...prev,
                    [tabKey]: {
                      ...initializedState,
                      phases: [
                        ...initializedState.phases,
                        {
                          ...phaseToDuplicate,
                          phase_name: `Phase ${visiblePhases.length + 1}`,
                          phase_id: newPhaseKey,
                          items: phaseToDuplicate.items.map((item) => ({
                            ...item,
                            item_id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                            phase_id: newPhaseKey,
                          })),
                        },
                      ],
                      activeAccordionKeys: [newPhaseKey],
                    },
                  }
                }
                return {
                  ...prev,
                  [tabKey]: {
                    ...prevState,
                    phases: duplicatePhaseAtIndex(
                      prevState.phases,
                      originalIdx,
                      newPhaseKey,
                    ),
                    activeAccordionKeys: [
                      ...prevState.activeAccordionKeys,
                      newPhaseKey,
                    ],
                  },
                }
              })
              setTimeout(() => {
                historyActionsRef.current.saveHistorySnapshot(tabKey)
                historyActionsRef.current.markHistorySavePending(tabKey)
              }, 0)
            }, 0)
          }
          break
        case 'edit':
          toggleEditModeRef.current(tabKey)
          break
        case 'delete':
          historyActionsRef.current.saveHistorySnapshot(tabKey)
          if (isEditMode) {
            setPendingPhaseDelete({
              tabKey,
              phaseIdx: originalIdx,
              phase,
              plan,
              isEditMode: true,
            })
          } else if (plan) {
            toggleEditModeRef.current(tabKey)
            setTimeout(() => {
              setPendingPhaseDelete({
                tabKey,
                phaseIdx: originalIdx,
                phase,
                plan,
                isEditMode: true,
              })
            }, 0)
          }
          break
        default:
          break
      }
    },
    [
      activeBillingDiscounts,
      handlePhaseMove,
      historyActionsRef,
      insuranceList,
      setFormStates,
      toggleEditModeRef,
    ],
  )

  const togglePhaseVisibility = useCallback((phaseId: string): void => {
    setPhaseVisibility((prev) => ({
      ...prev,
      [phaseId]: !(prev[phaseId] ?? true),
    }))
  }, [])

  const getPhaseVisibility = useCallback(
    (phaseId: string): boolean => {
      return phaseVisibility[phaseId] ?? true
    },
    [phaseVisibility],
  )

  const toggleAllPhaseAccordions = useCallback(
    (tabKey: string): void => {
      setFormStates((prev) => {
        const prevState = prev[tabKey]
        if (!prevState) return prev
        const visiblePhaseKeys = collectTxPlanVisiblePhaseAccordionKeys(
          prevState.phases,
        )
        const hasAnyExpanded = visiblePhaseKeys.some((key) =>
          prevState.activeAccordionKeys.includes(key),
        )
        return {
          ...prev,
          [tabKey]: {
            ...prevState,
            activeAccordionKeys: hasAnyExpanded ? [] : visiblePhaseKeys,
          },
        }
      })
    },
    [setFormStates],
  )

  const handleStartEditPhaseName = useCallback(
    (
      tabKey: string,
      phase: IPhases,
      originalIdx: number,
      currentName: string,
    ): void => {
      setEditingPhaseKey(getPhaseEditKey(tabKey, phase, originalIdx))
      setEditingPhaseValue(currentName)
    },
    [],
  )

  const handleSavePhaseName = useCallback(
    (tabKey: string, originalIdx: number, phaseIdx: number): void => {
      const trimmed = editingPhaseValue.trim()
      const nextName = trimmed !== '' ? trimmed : `Phase ${phaseIdx + 1}`
      historyActionsRef.current.queueHistorySnapshotBefore(tabKey)
      setFormStates((prev) => {
        const prevState = prev[tabKey]
        if (!prevState) return prev
        return {
          ...prev,
          [tabKey]: {
            ...prevState,
            phases: prevState.phases.map((p, i) =>
              i === originalIdx ? { ...p, phase_name: nextName } : p,
            ),
          },
        }
      })
      historyActionsRef.current.markHistorySavePending(tabKey)
      setEditingPhaseKey(null)
      setEditingPhaseValue('')
    },
    [editingPhaseValue, historyActionsRef, setFormStates],
  )

  const handleCancelEditPhaseName = useCallback((): void => {
    setEditingPhaseKey(null)
    setEditingPhaseValue('')
  }, [])

  return {
    phaseVisibility,
    phaseVisibilityRef,
    setPhaseVisibility,
    pendingPhaseDelete,
    setPendingPhaseDelete,
    editingPhaseKey,
    editingPhaseValue,
    setEditingPhaseValue,
    handleAddPhase,
    openProcedureModal,
    handleAddProcedureFromTooth,
    handleAddNewPhaseFromToothPicker,
    handleAddPhaseAndOpenProcedureModal,
    beginToothDrivenProcedureAdd,
    handleAddProcedure,
    confirmDeletePhase,
    handlePhaseStatusChange,
    handleMenuSelect,
    togglePhaseVisibility,
    getPhaseVisibility,
    toggleAllPhaseAccordions,
    handleStartEditPhaseName,
    handleSavePhaseName,
    handleCancelEditPhaseName,
  }
}
